import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {SalesPgActions} from './sales-pg.actions';
import {OfferPg} from '../../models/offer.model';
import {FormState} from '../../models/form-state.model';
import {SalesPgStep} from '../../enums/sales-pg-step.enum';
import {catchError, map, tap} from 'rxjs/operators';
import {SalesPgService} from '../../services/sales-pg.service';
import {PgCalculationRequest, PgCalculationResult, PgProductAvailableParams} from '../../models/sales-pg.model';
import {clearFormValidationErrors, setFormValidationErrors} from '../../helpers/form.helper';
import {EMPTY} from 'rxjs';
import {Clause} from '../../models/product.model';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {NavService} from '../../services/nav.service';
import {NotificationService} from '../../services/notification.service';
import {AvailableQuestionnaire, FilledQuestionnaire} from '../../models/questionnaire.model';

export interface SalesPgStateModel {
    dataForm: FormState;
    configurationForm: FormState;
    offer: OfferPg;
    activeStep: SalesPgStep;
    availability: PgProductAvailableParams;
    calculation: PgCalculationRequest;
    clauses: Clause[];
    questionnaire: AvailableQuestionnaire;
    filledQuestionnaire: FilledQuestionnaire;
    questionnaireForm: FormState;
}

@State<SalesPgStateModel>({
    name: SalesPgState.STATE_NAME,
    defaults: {
        dataForm: new FormState(),
        configurationForm: new FormState(),
        offer: null,
        activeStep: null,
        availability: null,
        calculation: {
            basicData: null,
            additionalData: null,
            model: null,
            results: null,
        },
        clauses: null,
        questionnaire: null,
        filledQuestionnaire: null,
        questionnaireForm: new FormState(),
    }
})
@Injectable()
export class SalesPgState {

    static readonly STATE_NAME = 'salesPg';

    constructor(
        private salesPgService: SalesPgService,
        private navService: NavService,
        private notificationService: NotificationService
    ) {
    }

    @Selector()
    static activeStep(state: SalesPgStateModel) {
        return state.activeStep;
    }

    @Selector()
    static availability(state: SalesPgStateModel) {
        return state.availability;
    }

    @Selector()
    static questionnaire(state: SalesPgStateModel) {
        return state.questionnaire;
    }

    @Selector()
    static questionnaireForm(state: SalesPgStateModel) {
        return state.questionnaireForm;
    }

    @Selector()
    static clauses(state: SalesPgStateModel) {
        return state.clauses;
    }

    @Selector()
    static dataFormErrors(state: SalesPgStateModel) {
        return state.dataForm.validationErrors;
    }

    @Selector()
    static configurationFormErrors(state: SalesPgStateModel) {
        return state.configurationForm.validationErrors;
    }

    @Selector()
    static calculation(state: SalesPgStateModel) {
        return state.calculation;
    }

    @Action(SalesPgActions.GetAvailability)
    getAvailability(ctx: StateContext<SalesPgStateModel>) {
        return this.salesPgService.getAvailability().pipe(
            tap((availability: PgProductAvailableParams) => {
                ctx.patchState({availability});
            })
        );
    }

    @Action(SalesPgActions.GetQuestionnaire)
    getQuestionnaire(ctx: StateContext<SalesPgStateModel>) {
        return this.salesPgService.getQuestionnaire().pipe(
            tap((questionnaire: AvailableQuestionnaire) => {
                if (questionnaire?.questions?.length > 0) {
                    ctx.patchState({questionnaire});
                }else {
                    ctx.patchState({
                        questionnaire: null,
                        filledQuestionnaire: null,
                    });
                }
            }),
            catchError((errorResponse) => {
                if (errorResponse?.status === 404) {
                    ctx.patchState({
                        questionnaire: null,
                        filledQuestionnaire: null,
                    });
                }
                return EMPTY;
            })
        );
    }

    @Action(SalesPgActions.SetActiveStep)
    getOffer(ctx: StateContext<SalesPgStateModel>, {activeStep}: SalesPgActions.SetActiveStep) {
        clearFormValidationErrors(ctx, [], 'configurationForm');
        clearFormValidationErrors(ctx, [], 'dataForm');
        ctx.patchState({
            activeStep,
            clauses: null,
        });
    }

    @Action(SalesPgActions.CalculationRequest)
    calculationRequest(ctx: StateContext<SalesPgStateModel>, {payload}: SalesPgActions.CalculationRequest) {
        const questionnaire = ctx.getState()?.filledQuestionnaire;
        return this.salesPgService.calculate({...payload, questionnaire}).pipe(
            map((results) => {
                clearFormValidationErrors(ctx, [], 'dataForm');
                ctx.patchState({
                    calculation: {
                        ...payload,
                        results: results.map(item => new PgCalculationResult(item)),
                    }
                });
                return Array.isArray(results) && results.length > 0
                    ? ctx.dispatch(new SalesPgActions.SetActiveStep(SalesPgStep.CONFIGURATION))
                    : EMPTY;
            }),
            catchError(({error}) => {
                setFormValidationErrors(ctx, error, SalesPgState.STATE_NAME, 'dataForm');
                return EMPTY;
            })
        );
    }

    @Action(SalesPgActions.ClausesRequest)
    clausesRequest(ctx: StateContext<SalesPgStateModel>, {payload}: SalesPgActions.ClausesRequest) {
        return this.salesPgService.getClauses(payload).pipe(
            tap((clauses: Clause[]) => {
                ctx.patchState({
                    clauses: clauses.map(item => new Clause(item))
                });
            })
        );
    }

    @Action(SalesPgActions.CreatePolicyRequest)
    createPolicyRequest(ctx: StateContext<SalesPgStateModel>, {payload}: SalesPgActions.CreatePolicyRequest) {
        const questionnaire = ctx.getState()?.filledQuestionnaire;
        return this.salesPgService.createPolicy({...payload, questionnaire}).pipe(
            tap((response) => {
                clearFormValidationErrors(ctx, [], 'configurationForm');
                this.navService.navigateByCode(NavItemCodes.POLICY_PG, {policyId: response.id});
            }),
            catchError(({error}) => {
                setFormValidationErrors(ctx, error, SalesPgState.STATE_NAME, 'configurationForm');
                const nullErrors = ctx.getState().configurationForm?.validationErrors?.null;
                if (Array.isArray(nullErrors) && nullErrors.length) {
                    this.notificationService.error(nullErrors[0]);
                }
                return EMPTY;
            })
        );
    }

    @Action(SalesPgActions.ClearForm)
    clearForm(ctx: StateContext<SalesPgStateModel>) {
        ctx.patchState({
            dataForm: new FormState(),
            configurationForm: new FormState(),
            questionnaire: null,
            filledQuestionnaire: null,
            questionnaireForm: new FormState(),
        });
    }

    @Action(SalesPgActions.ValidateQuestionnaire)
    validateQuestionnaire(ctx: StateContext<SalesPgStateModel>, {payload}: SalesPgActions.ValidateQuestionnaire) {
        return this.salesPgService.validateQuestionnaire(payload).pipe(
            map(() => {
                clearFormValidationErrors(ctx, [], 'questionnaireForm');
                ctx.patchState({
                    filledQuestionnaire: payload,
                });
                ctx.dispatch(new SalesPgActions.SetActiveStep(SalesPgStep.DATA));
            }),
            catchError(({error}) => {
                setFormValidationErrors(ctx, error, SalesPgState.STATE_NAME, 'questionnaireForm');
                return EMPTY;
            })
        );
    }
}
